<template>
  <section class="hero-section relative overflow-hidden">
    <!-- Background -->
    <div class="absolute inset-0 w-full h-full overflow-hidden z-0">
      <!-- Video Background -->
      <video 
        class="absolute w-full h-full object-cover"
        autoplay 
        loop 
        muted 
        playsinline
      >
        <source src="@/assets/فديو الشعار.mp4" type="video/mp4">
      </video>
      <div class="absolute inset-0 bg-gradient-to-br from-[#353899] to-[#3a3494] opacity-80"></div>
      <div class="absolute inset-0 bg-[#353899]/20 backdrop-blur-sm"></div>
    </div>

    <!-- Background Elements -->
    <div class="absolute inset-0 bg-[url('@/assets/grid-pattern.svg')] opacity-20 z-1"></div>
    
    <!-- Animated Shapes -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-1">
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
    </div>

    <!-- Content Container -->
    <div class="container mx-auto px-4 relative z-10 flex items-center min-h-screen pt-[var(--header-height)]">
      <div class="flex flex-wrap items-center justify-between w-full gap-8">
        <!-- Text Content -->
        <div class="w-full lg:w-5/12 space-y-8" :class="{ 'lg:order-2': currentLang === 'ar' }">
          <h1 class="text-6xl lg:text-7xl font-bold text-white animate-fade-in">
            {{ $t('hero.title') }}
          </h1>
          <p class="text-xl lg:text-2xl text-white/90 animate-fade-in-delay">
            {{ $t('hero.description') }}
          </p>
          <div class="flex flex-wrap gap-4">
            <a href="https://admin.salasah.sa/#/login" class="btn-primary">
              {{ $t('hero.cta.primary') }}
            </a>
          </div>
        </div>

        <!-- Dashboard Preview -->
        <div class="relative w-full lg:w-6/12 dashboard-container" :class="{ 'lg:order-1': currentLang === 'ar' }">
          <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl bg-white/5 backdrop-blur-sm p-4">
            <!-- Main Image -->
            <div class="relative">
              <img 
                :src="require('@/assets/Agile.png')"
                alt="Agile Project Management"
                class="w-full h-auto rounded-xl"
                @error="handleImageError"
              >
              <!-- System Integration Card -->
              <div class="floating-card absolute top-0 -left-8 lg:-left-16 bg-[#353899]/90 backdrop-blur p-4 lg:p-6 rounded-2xl shadow-xl animate-float-delay-1">
                <div class="card-content text-center">
                  <div class="icon-wrapper mb-2">
                    <i class="fas fa-chart-line text-2xl lg:text-3xl text-white"></i>
                  </div>
                  <div class="text-3xl lg:text-4xl font-bold text-white mb-1">15+</div>
                  <div class="text-white/90 text-sm lg:text-base">نظام متكامل</div>
                </div>
              </div>
              <!-- Clients Card -->
              <div class="floating-card absolute -bottom-8 lg:-bottom-12 right-8 lg:right-12 bg-[#3a3494]/90 backdrop-blur p-4 lg:p-6 rounded-2xl shadow-xl animate-float-delay-2">
                <div class="card-content text-center">
                  <div class="icon-wrapper mb-2">
                    <i class="fas fa-users text-2xl lg:text-3xl text-white"></i>
                  </div>
                  <div class="text-white/90 text-sm lg:text-base">عميل</div>
                  <div class="text-3xl lg:text-4xl font-bold text-white">+1000</div>
                </div>
              </div>
            </div>
          </div>
          <!-- Decorative Elements -->
          <div class="absolute -top-10 -right-10 w-64 h-64 bg-[#32399f] rounded-full filter blur-3xl opacity-30"></div>
          <div class="absolute -bottom-10 -left-10 w-64 h-64 bg-[#3a3494] rounded-full filter blur-3xl opacity-30"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'HeroSection',
  setup() {
    const { locale } = useI18n()
    const currentLang = computed(() => locale.value)

    const handleImageError = (e) => {
      // If image fails to load, hide it
      e.target.style.display = 'none'
    }

    onMounted(() => {
      // Handle video autoplay on mobile devices
      const video = document.querySelector('video')
      if (video) {
        video.play().catch((error) => {
          console.log("Video autoplay failed:", error)
        })
      }
    })

    return {
      currentLang,
      handleImageError
    }
  }
}
</script>

<style scoped>
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
}

.text-gradient {
  @apply bg-clip-text text-transparent bg-gradient-to-r;
  background-image: linear-gradient(135deg, #ffffff, #e0e7ff);
}

.btn-primary {
  @apply px-8 py-3 bg-white text-[#353899] rounded-lg font-semibold 
         transition-all duration-300 hover:shadow-lg hover:shadow-white/20 
         transform hover:-translate-y-0.5;
}

.btn-secondary {
  @apply px-8 py-3 bg-white/10 text-white rounded-lg font-semibold 
         transition-all duration-300 hover:bg-white/20 
         backdrop-filter backdrop-blur-sm border border-white/20;
}

.stat-card {
  @apply bg-white/5 backdrop-blur-sm rounded-xl p-6 
         flex flex-col items-center justify-center text-center
         transform transition-all duration-300 hover:-translate-y-1;
}

.dashboard-container {
  @apply relative transform transition-all duration-500;
  perspective: 1000px;
}

.dashboard-container:hover {
  transform: translateY(-10px) rotateY(2deg);
}

/* Floating Shapes Animation */
.floating-shape {
  @apply absolute rounded-full opacity-20 backdrop-blur-3xl;
  animation: float 20s infinite;
}

.shape-1 {
  @apply w-96 h-96 bg-[#32399f];
  top: -10%;
  left: -5%;
  animation-delay: 0s;
}

.shape-2 {
  @apply w-72 h-72 bg-[#3a3494];
  top: 40%;
  right: -10%;
  animation-delay: -5s;
}

.shape-3 {
  @apply w-64 h-64 bg-[#353899];
  bottom: -10%;
  left: 30%;
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
  }
}

/* Floating Cards Animation */
.floating-card {
  @apply transform transition-all duration-500;
  animation: float 6s ease-in-out infinite;
  box-shadow: 0 8px 32px rgba(53, 56, 153, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.floating-card .icon-wrapper {
  width: 48px;
  height: 48px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.floating-card:hover .icon-wrapper {
  transform: scale(1.1) rotate(10deg);
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.floating-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(53, 56, 153, 0.4);
  background-opacity: 1;
}

/* Update animation timing */
.animate-float-delay-1 {
  animation-delay: -1s;
}

.animate-float-delay-2 {
  animation-delay: -2s;
}

/* Fade In Animations */
.animate-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-fade-in-delay {
  animation: fadeIn 0.8s ease-out 0.2s forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-section {
    text-align: center;
  }
  
  .flex.gap-4 {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  h1 {
    font-size: 2.5rem;
  }
  
  .floating-shape {
    display: none;
  }
  
  .dashboard-container {
    margin-top: 2rem;
  }
  
  .floating-card {
    padding: 1rem;
  }
  
  .floating-card .icon-wrapper {
    width: 36px;
    height: 36px;
  }
  
  .floating-card i {
    font-size: 1.25rem;
  }
  
  .hero-section video {
    object-position: center;
  }
}

/* Video Background Styles */
.hero-section video {
  object-fit: cover;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  transform: scale(1.01); /* Prevent white edges during video playback */
  filter: brightness(0.8) contrast(1.1); /* Enhance video appearance */
  will-change: transform; /* Optimize performance */
}
</style> 